import React, { useState, useEffect } from 'react';
import PocketBase from 'pocketbase';
import Footer from '../Footer';

const pb = new PocketBase('https://bahcarplaza.com');

function CheckoutPage({ setView, selectedCar, setActiveTab }) {
  const [step, setStep] = useState(1);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false); 
  
  const [useWallet, setUseWallet] = useState(false);
  // 🚨 NEW: Payment Method State ('Online' or 'COD') 🚨
  const [paymentMethodType, setPaymentMethodType] = useState('Online');

  useEffect(() => {
    window.scrollTo(0, 0);

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (!pb.authStore.isValid) { setView('auth'); return; }
    if (!selectedCar) { setView('home'); return; }
    
    const fetchUser = async () => {
      try {
        const user = await pb.collection('users').getOne(pb.authStore.model.id);
        setUserData(user);
        setLoading(false);
      } catch (error) { 
        console.error(error); 
        setLoading(false); 
      }
    };
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCar?.id]);

  if (loading || !selectedCar) return <div style={{ minHeight: '100vh', backgroundColor: '#f4f7f6', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><h2 style={{color: '#002f34'}}>Loading Checkout...</h2></div>;

  const getVal = (val) => (Array.isArray(val) ? val[0] : val || "");
  const carImage = `https://bahcarplaza.com/api/files/${selectedCar.collectionId}/${selectedCar.id}/${getVal(selectedCar.image)}`;

  const carPrice = selectedCar.price || 0;
  const platformFee = 999;
  const subTotal = carPrice + platformFee;
  
  const walletBalance = userData?.wallet_balance || 0;
  const walletDeduction = useWallet ? Math.min(walletBalance, subTotal) : 0;
  const finalAmount = subTotal - walletDeduction;

  // 🚨 NEW: Calculate Token Amount for COD 🚨
  const tokenLimit = 10000;
  const payableAmount = paymentMethodType === 'COD' && finalAmount > 0 ? Math.min(tokenLimit, finalAmount) : finalAmount;
  const balanceOnDelivery = paymentMethodType === 'COD' ? finalAmount - payableAmount : 0;

  // Auto-switch to 'Online' if finalAmount is 0 (fully paid by wallet)
  if (finalAmount === 0 && paymentMethodType === 'COD') {
    setPaymentMethodType('Online');
  }

  const handleNextStep = () => {
    if (step === 2) {
      if (!userData?.address_line || !userData?.pin_code) {
        alert("Please go to 'My Account' and update your complete address before proceeding.");
        setView('account');
        return;
      }
    }
    setStep(step + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' }); 
  };

  const handlePayment = async () => {
    setIsProcessing(true);
    
    // 🚨 ডাইনামিক অ্যাড্রেস তৈরি 🚨
    const fullAddress = `${userData.address_line}, ${userData.landmark ? userData.landmark + ', ' : ''}${userData.city}, ${userData.state} - ${userData.pin_code}`;
    const currentDateTime = new Date().toISOString();

    let dbPaymentMethod = 'Card'; // Default for Razorpay Online
    if (finalAmount === 0) dbPaymentMethod = 'Wallet';
    else if (paymentMethodType === 'COD') dbPaymentMethod = 'Cash on Delivery';

    let orderRecord;
    try {
      // 🚨 ডাটাবেসের সবগুলো ফিল্ড পপুলেট করে প্রাথমিক অর্ডার তৈরি 🚨
      orderRecord = await pb.collection('orders').create({ 
        user_id: pb.authStore.model.id, 
        car_id: selectedCar.id, 
        amount: carPrice,
        tax_amount: platformFee,
        final_amount: finalAmount,
        payment_status: 'Pending',
        order_status: 'Pending',
        order_date: currentDateTime,
        delivery_address: fullAddress,
        payment_method: dbPaymentMethod
      });
    } catch (e) {
      alert("Failed to initialize order: " + e.message);
      setIsProcessing(false);
      return;
    }

    // 🚨 লজিক ১: যদি ১০০% পেমেন্ট Wallet দিয়ে হয় 🚨
    if (finalAmount === 0 || payableAmount === 0) {
      try {
        await pb.collection('orders').update(orderRecord.id, { 
          payment_status: 'Processing',
          order_status: 'Confirmed',
          paid_at: currentDateTime,
          transaction_id: `WALLET_TXN_${Date.now()}`,
          notes: `Paid fully via Wallet balance. Deducted: ₹${walletDeduction.toLocaleString()}`
        });
        
        const newBalance = Number(walletBalance) - Number(walletDeduction);
        const updatedUser = await pb.collection('users').update(pb.authStore.model.id, { wallet_balance: newBalance });
        pb.authStore.save(pb.authStore.token, updatedUser);

        try { await pb.collection('cars').update(selectedCar.id, { status: 'Sold' }); } catch (err) {}

        setPaymentSuccess(true); 
        setIsProcessing(false);
        setTimeout(() => { if (setActiveTab) setActiveTab('My Orders'); setView('account'); }, 3000);
        return;
      } catch (err) {
        console.error("Order/Wallet Update Error:", err);
        alert("Failed to process wallet payment: " + err.message);
        setIsProcessing(false);
        return;
      }
    }

    // 🚨 লজিক ২: যদি Razorpay (Full Online বা COD Token) দিয়ে পেমেন্ট হয় 🚨
    try {
      const response = await fetch('https://bahcarplaza.com/payment/api/razorpay/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: payableAmount, // 👈 Token amount or Full amount goes to Razorpay
          receiptId: `receipt_${orderRecord.id}`
        })
      });

      const data = await response.json();

      if (!data.success) {
        alert("Payment Initialization Failed!");
        setIsProcessing(false);
        return;
      }

      // Razorpay-এর জেনারেট করা Order ID ডাটাবেসে সেভ করা
      await pb.collection('orders').update(orderRecord.id, {
        razorpay_order_id: data.order.id
      });

      const options = {
        key: data.keyId, 
        amount: data.order.amount, 
        currency: "INR",
        name: "Bah! Car Plaza",
        description: paymentMethodType === 'COD' ? `Token Payment for ${selectedCar.brand}` : `Payment for ${selectedCar.brand}`,
        image: "https://cdn-icons-png.flaticon.com/512/3202/3202926.png",
        order_id: data.order.id, 
        handler: async (response) => {
          
          // 🚨 পেমেন্ট সাকসেস হলে Notes তৈরি এবং সব ফিল্ড আপডেট 🚨
          const paymentNotes = paymentMethodType === 'COD' 
            ? `Paid ₹${payableAmount.toLocaleString()} token via Razorpay. Balance ₹${balanceOnDelivery.toLocaleString()} to be paid on delivery. Wallet deducted: ₹${walletDeduction.toLocaleString()}`
            : `Paid full balance ₹${payableAmount.toLocaleString()} via Razorpay. Wallet deducted: ₹${walletDeduction.toLocaleString()}`;

          try {
            await pb.collection('orders').update(orderRecord.id, { 
              payment_status: 'Processing',
              order_status: 'Confirmed',
              paid_at: new Date().toISOString(),
              transaction_id: response.razorpay_payment_id,
              notes: paymentNotes
            });
          } catch (err) { console.error("Order Update Error:", err); }

          try { await pb.collection('cars').update(selectedCar.id, { status: 'Sold' }); } catch (err) {}
          
          if (useWallet && walletDeduction > 0) {
            try {
              const newBalance = Number(walletBalance) - Number(walletDeduction);
              const updatedUser = await pb.collection('users').update(pb.authStore.model.id, { wallet_balance: newBalance });
              pb.authStore.save(pb.authStore.token, updatedUser);
            } catch (err) {}
          }

          setPaymentSuccess(true); 
          setIsProcessing(false);
          setTimeout(() => { if (setActiveTab) setActiveTab('My Orders'); setView('account'); }, 3000);
        },
        modal: { ondismiss: function() { setIsProcessing(false); } },
        prefill: { name: userData?.name || "", email: userData?.email || "", contact: userData?.phone || "" },
        theme: { color: "#002f34" }
      };

      const razorpayObject = new window.Razorpay(options);
      
      razorpayObject.on('payment.failed', async function (response) {
        await pb.collection('orders').update(orderRecord.id, { payment_status: 'Failed' });
        alert("❌ Payment Failed. Please try again.");
        setIsProcessing(false);
      });

      razorpayObject.open();

    } catch (error) {
      console.error(error);
      alert("Server Error! Could not connect to payment gateway.");
      setIsProcessing(false);
    }
  };

  return (
    <div style={{ backgroundColor: '#f4f7f6', minHeight: '100vh', fontFamily: 'sans-serif', display: 'flex', flexDirection: 'column' }}>
      
      <style>
        {`
          .checkout-container { max-width: 1200px; width: 100%; margin: 40px auto; padding: 0 20px; display: flex; gap: 40px; align-items: flex-start; }
          .checkout-left { flex: 1; display: flex; flexDirection: column; gap: 20px; }
          .checkout-right { width: 380px; position: sticky; top: 40px; }
          
          .step-card { background: #fff; border-radius: 25px; padding: 30px; box-shadow: 0 5px 20px rgba(0,0,0,0.03); border: 1px solid #eee; margin-bottom: 20px; transition: 0.3s; }
          .step-card.inactive { opacity: 0.6; pointer-events: none; filter: grayscale(100%); }
          
          .step-header { display: flex; align-items: center; gap: 15px; margin-bottom: 20px; border-bottom: 2px solid #f9f9f9; padding-bottom: 15px; }
          .step-number { background: #002f34; color: #fff; width: 35px; height: 35px; border-radius: 50%; display: flex; justify-content: center; align-items: center; font-weight: 900; font-size: 16px; }
          .step-title { font-size: 20px; font-weight: 900; color: #002f34; margin: 0; text-transform: uppercase; }

          .summary-card { background: #fff; border-radius: 25px; padding: 30px; box-shadow: 0 10px 30px rgba(0,0,0,0.05); border: 1px solid #eee; }
          .price-row { display: flex; justify-content: space-between; margin-bottom: 15px; font-size: 14px; color: #555; font-weight: bold; }
          .price-row.total { border-top: 2px dashed #eee; padding-top: 20px; margin-top: 10px; font-size: 20px; color: #002f34; font-weight: 900; }

          .primary-btn { width: 100%; padding: 18px; background: #ef5e33; color: #fff; border: none; border-radius: 15px; font-weight: 900; font-size: 16px; text-transform: uppercase; cursor: pointer; box-shadow: 0 8px 20px rgba(239,94,51,0.3); transition: 0.3s; }
          .primary-btn:hover { background: #d94f29; transform: translateY(-2px); }
          .primary-btn:disabled { background: #ccc; cursor: not-allowed; transform: none; box-shadow: none; }

          .mobile-sticky-footer { display: none; }

          @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          @keyframes popIn { 0% { transform: scale(0.8); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }

          @media (max-width: 900px) {
            .checkout-container { flex-direction: column; margin: 20px auto 100px; gap: 20px; }
            .checkout-right { width: 100%; position: static; }
            .step-card, .summary-card { padding: 20px; border-radius: 20px; }
            
            .mobile-sticky-footer { display: flex; position: fixed; bottom: 0; left: 0; right: 0; background: #fff; padding: 15px 20px; box-shadow: 0 -5px 20px rgba(0,0,0,0.1); z-index: 100; justify-content: space-between; align-items: center; border-top-left-radius: 25px; border-top-right-radius: 25px; }
            .desktop-btn { display: none; }
          }
        `}
      </style>

      <div style={{ background: '#002f34', padding: '15px 20px', color: '#fff', display: 'flex', alignItems: 'center', gap: '15px', position: 'sticky', top: 0, zIndex: 50 }}>
        <button onClick={() => setView('details')} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: '40px', height: '40px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>❮</button>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '900', textTransform: 'uppercase', fontStyle: 'italic' }}>Secure Checkout</h2>
      </div>

      <div className="checkout-container">
        
        <div className="checkout-left">
          
          <div className={`step-card ${step < 1 ? 'inactive' : ''}`}>
            <div className="step-header">
              <div className="step-number" style={{ background: step > 1 ? '#1db954' : '#002f34' }}>{step > 1 ? '✓' : '1'}</div>
              <h2 className="step-title">Review Order</h2>
            </div>
            
            <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
              <img src={carImage} style={{ width: '120px', height: '90px', borderRadius: '15px', objectFit: 'cover' }} alt="Car" />
              <div>
                <h3 style={{ margin: '0 0 5px 0', color: '#002f34', fontWeight: '900', fontSize: '20px', textTransform: 'uppercase' }}>{selectedCar.brand} {selectedCar.model}</h3>
                <p style={{ margin: 0, color: '#666', fontSize: '13px', fontWeight: 'bold' }}>{selectedCar.year} • {selectedCar.kilometers_driven?.toLocaleString()} KM • {getVal(selectedCar.fuel_type)}</p>
                <p style={{ margin: '5px 0 0 0', color: '#ef5e33', fontWeight: '900', fontSize: '18px' }}>₹{carPrice.toLocaleString()}</p>
              </div>
            </div>
            
            {step === 1 && (
              <button className="primary-btn desktop-btn" style={{ marginTop: '20px' }} onClick={handleNextStep}>Proceed to Address</button>
            )}
          </div>

          <div className={`step-card ${step < 2 ? 'inactive' : ''}`}>
            <div className="step-header">
              <div className="step-number" style={{ background: step > 2 ? '#1db954' : '#002f34' }}>{step > 2 ? '✓' : '2'}</div>
              <h2 className="step-title">Delivery Address</h2>
            </div>
            
            {userData?.address_line ? (
              <div style={{ backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '15px', border: '1px solid #eee' }}>
                <p style={{ margin: '0 0 5px 0', fontWeight: '900', color: '#002f34', fontSize: '16px', textTransform: 'uppercase' }}>{userData.name}</p>
                <p style={{ margin: '0 0 10px 0', color: '#555', fontSize: '13px', lineHeight: '1.5', fontWeight: 'bold' }}>
                  {userData.address_line}<br/>
                  {userData.landmark && <>{userData.landmark}<br/></>}
                  {userData.city}, {userData.state} - {userData.pin_code}
                </p>
                <p style={{ margin: 0, color: '#666', fontSize: '12px', fontWeight: 'bold' }}>📞 +91 {userData.phone}</p>
                <button onClick={() => { setView('account'); if(setActiveTab) setActiveTab('My Address'); }} style={{ background: 'none', border: 'none', color: '#ef5e33', fontWeight: '900', fontSize: '12px', cursor: 'pointer', marginTop: '15px', padding: 0, textDecoration: 'underline' }}>Change Address</button>
              </div>
            ) : (
              <div style={{ backgroundColor: '#fff4e6', padding: '20px', borderRadius: '15px', color: '#ef5e33', fontWeight: 'bold', fontSize: '13px', textAlign: 'center' }}>
                ⚠️ No address found. Please update your address in 'My Account' to proceed.
                <button onClick={() => { setView('account'); if(setActiveTab) setActiveTab('My Address'); }} style={{ display: 'block', margin: '15px auto 0', padding: '10px 20px', backgroundColor: '#002f34', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer' }}>Go to Account</button>
              </div>
            )}

            {step === 2 && userData?.address_line && (
              <button className="primary-btn desktop-btn" style={{ marginTop: '20px' }} onClick={handleNextStep}>Proceed to Payment</button>
            )}
          </div>

          <div className={`step-card ${step < 3 ? 'inactive' : ''}`}>
            <div className="step-header">
              <div className="step-number" style={{ background: '#002f34' }}>3</div>
              <h2 className="step-title">Payment Method</h2>
            </div>
            
            {/* 🚨 NEW: Payment Options UI (Online vs COD) 🚨 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <label style={{ border: paymentMethodType === 'Online' ? '2px solid #3399cc' : '1px solid #eee', padding: '20px', borderRadius: '15px', backgroundColor: paymentMethodType === 'Online' ? '#f0f8ff' : '#fff', display: 'flex', alignItems: 'center', gap: '15px', cursor: 'pointer', transition: '0.3s' }}>
                <input type="radio" name="paymentType" checked={paymentMethodType === 'Online'} onChange={() => setPaymentMethodType('Online')} style={{ width: '20px', height: '20px', accentColor: '#3399cc' }} />
                <div>
                  <h4 style={{ margin: 0, color: '#002f34', fontWeight: '900', fontSize: '16px' }}>Pay Online (Full Amount)</h4>
                  <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '11px', fontWeight: 'bold' }}>Razorpay Secure (UPI, Card, Net Banking)</p>
                </div>
              </label>

              {finalAmount > 0 && (
                <label style={{ border: paymentMethodType === 'COD' ? '2px solid #ef5e33' : '1px solid #eee', padding: '20px', borderRadius: '15px', backgroundColor: paymentMethodType === 'COD' ? '#fff4e6' : '#fff', display: 'flex', alignItems: 'center', gap: '15px', cursor: 'pointer', transition: '0.3s' }}>
                  <input type="radio" name="paymentType" checked={paymentMethodType === 'COD'} onChange={() => setPaymentMethodType('COD')} style={{ width: '20px', height: '20px', accentColor: '#ef5e33' }} />
                  <div>
                    <h4 style={{ margin: 0, color: '#002f34', fontWeight: '900', fontSize: '16px' }}>Cash on Delivery (Pay Token)</h4>
                    <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '11px', fontWeight: 'bold' }}>Pay ₹10,000 now to confirm. Pay balance on delivery.</p>
                  </div>
                </label>
              )}
            </div>

            {step === 3 && (
              <button disabled={isProcessing} className="primary-btn desktop-btn" style={{ marginTop: '25px', backgroundColor: isProcessing ? '#ccc' : (paymentMethodType === 'Online' ? '#3399cc' : '#ef5e33'), boxShadow: isProcessing ? 'none' : '0 8px 20px rgba(0,0,0,0.1)' }} onClick={handlePayment}>
                {isProcessing ? 'Processing...' : (payableAmount === 0 ? `✨ Complete Order` : `🔒 Pay ₹${payableAmount.toLocaleString()} Now`)}
              </button>
            )}
          </div>

        </div>

        <div className="checkout-right">
          <div className="summary-card">
            <h2 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '900', color: '#002f34', textTransform: 'uppercase', borderBottom: '2px solid #eee', paddingBottom: '15px' }}>Order Summary</h2>
            
            <div className="price-row">
              <span>Car Value</span>
              <span>₹{carPrice.toLocaleString()}</span>
            </div>
            <div className="price-row">
              <span>Platform Fee</span>
              <span>₹{platformFee.toLocaleString()}</span>
            </div>
            
            {walletBalance > 0 && (
              <div style={{ backgroundColor: useWallet ? '#f5fff9' : '#f9f9f9', padding: '15px', borderRadius: '12px', border: useWallet ? '2px solid #1db954' : '1px solid #ddd', marginTop: '15px', transition: '0.3s' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', color: '#002f34', fontWeight: '900', fontSize: '13px' }}>
                  <input type="checkbox" checked={useWallet} onChange={(e) => setUseWallet(e.target.checked)} style={{ accentColor: '#1db954', width: '18px', height: '18px', cursor: 'pointer' }} />
                  Use Wallet Balance (₹{walletBalance.toLocaleString()})
                </label>
                {useWallet && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', fontSize: '14px', fontWeight: '900', color: '#1db954' }}>
                    <span>Wallet Applied</span>
                    <span>- ₹{walletDeduction.toLocaleString()}</span>
                  </div>
                )}
              </div>
            )}
            
            <div className="price-row total" style={{ borderTop: paymentMethodType === 'COD' ? '2px dashed #eee' : '2px dashed #eee', marginBottom: paymentMethodType === 'COD' ? '5px' : '0' }}>
              <span>Total Value</span>
              <span style={{ color: '#002f34' }}>₹{finalAmount.toLocaleString()}</span>
            </div>

            {/* 🚨 NEW: COD Breakdown in Summary 🚨 */}
            {paymentMethodType === 'COD' && finalAmount > 0 && (
              <>
                <div className="price-row" style={{ color: '#ef5e33', fontSize: '16px', marginTop: '10px' }}>
                  <span>Token to Pay Now</span>
                  <span>₹{payableAmount.toLocaleString()}</span>
                </div>
                <div className="price-row" style={{ color: '#666', fontSize: '12px' }}>
                  <span>Balance on Delivery</span>
                  <span>₹{balanceOnDelivery.toLocaleString()}</span>
                </div>
              </>
            )}

            <p style={{ fontSize: '10px', color: '#aaa', textAlign: 'center', marginTop: '20px', fontWeight: 'bold' }}>
              By proceeding, you agree to our Terms & Conditions and Privacy Policy. Secure transaction via Razorpay.
            </p>
          </div>
        </div>

      </div>

      <div className="mobile-sticky-footer">
        <div>
          <p style={{ margin: 0, fontSize: '10px', fontWeight: 'bold', color: '#888', textTransform: 'uppercase' }}>
            {paymentMethodType === 'COD' ? 'Token Payable' : 'Total Payable'}
          </p>
          <h3 style={{ margin: 0, fontSize: '22px', fontWeight: '900', color: '#002f34' }}>₹{payableAmount.toLocaleString()}</h3>
        </div>
        
        {step === 1 && <button onClick={handleNextStep} style={{ background: '#ef5e33', color: '#fff', border: 'none', padding: '15px 30px', borderRadius: '15px', fontWeight: '900', fontSize: '14px', textTransform: 'uppercase', boxShadow: '0 5px 15px rgba(239,94,51,0.3)' }}>Next</button>}
        
        {step === 2 && userData?.address_line && <button onClick={handleNextStep} style={{ background: '#ef5e33', color: '#fff', border: 'none', padding: '15px 30px', borderRadius: '15px', fontWeight: '900', fontSize: '14px', textTransform: 'uppercase', boxShadow: '0 5px 15px rgba(239,94,51,0.3)' }}>Payment</button>}
        
        {step === 3 && <button disabled={isProcessing} onClick={handlePayment} style={{ background: isProcessing ? '#ccc' : (paymentMethodType === 'Online' ? '#3399cc' : '#ef5e33'), color: '#fff', border: 'none', padding: '15px 30px', borderRadius: '15px', fontWeight: '900', fontSize: '14px', textTransform: 'uppercase', boxShadow: isProcessing ? 'none' : '0 5px 15px rgba(0,0,0,0.1)' }}>{isProcessing ? 'Wait...' : (payableAmount === 0 ? 'Place Order' : 'Pay Now')}</button>}
      </div>

      {paymentSuccess && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,47,52,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', backdropFilter: 'blur(5px)' }}>
          <div style={{ background: '#fff', width: '100%', maxWidth: '400px', borderRadius: '30px', padding: '40px 30px', textAlign: 'center', boxShadow: '0 20px 50px rgba(0,0,0,0.5)', animation: 'popIn 0.5s ease-out forwards' }}>
            <div style={{ fontSize: '70px', marginBottom: '10px' }}>🎉</div>
            <h2 style={{ color: '#1db954', fontWeight: '900', margin: '0 0 10px 0', fontSize: '28px', textTransform: 'uppercase' }}>Order Successful!</h2>
            <p style={{ color: '#666', fontWeight: 'bold', fontSize: '14px', marginBottom: '30px', lineHeight: '1.5' }}>
              Your order has been placed securely. You are being redirected to your orders...
            </p>
            <div style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #1db954', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }}></div>
          </div>
        </div>
      )}

      <Footer setView={setView} />
    </div>
  );
}

export default CheckoutPage;